import { useQuery } from '@apollo/client';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import { NoData, PageHeader, PrimaryButton } from '../../components';
import { GetStudentPapersQuery } from '../../generated/graphql';
import { academicYear, PageLoader } from '../../helpers';
import { useExamsTypeAndSem } from '../../helpers/examTypeAndSemester';
import { GET_STD_TIME_TABLE } from './graphql';
import {
  DetailItem,
  Details,
  PageWrapper,
  Row,
  Title,
  PageHeaderWrap,
  DetailItemkey,
  QRcode,
  PrintButton,
  TimetableWrap,
} from './styles';

export const StudentHome = (): JSX.Element => {
  const timetableRef = React.useRef(null);
  const examTypeAndSemester = useExamsTypeAndSem();
  const handlePrint = useReactToPrint({
    content: () => timetableRef.current,
    documentTitle: `my-${academicYear}-${examTypeAndSemester?.examType}
    -${examTypeAndSemester?.semester}
    -exams-timetable`,
    pageStyle: 'lg:px-48 px-20',
    onPrintError: () => toast.error('Printing of timetable failed!'),
  });
  const { loading, data, error } = useQuery<GetStudentPapersQuery>(GET_STD_TIME_TABLE);

  if (loading) return <PageLoader />;

  if (error || data?.getStudentPapers?.length === 0 || !data?.getStudentPapers) {
    toast.error(
      `Error occured in fetching your schedule, 
       please try and login in again or visit exams office`,
      {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      },
    );

    return (
      <div className="lg:mt-10 h-screen flex flex-col justify-center items-center">
        <NoData
          title="your schedule was not found"
          subtitle="Please visit the exams office to resolve this issue"
        />
        <Link className="text-blue-600 text-xl tracking-wide text-ellipsis underline" to="/">
          Go Home
        </Link>
      </div>
    );
  }

  return (
    <PageWrapper>
      <Helmet>
        <title>KNUST, CoE Check timetable for Student</title>
        <meta
          name="check-timetable"
          content="Home page for KNUST,college of engineering students to check timetable schedule"
        />
      </Helmet>
      <PageHeaderWrap>
        <PageHeader />
      </PageHeaderWrap>
      <div
        className="text-center bg-white p-4
       mt-10 flex flex-col gap-3 justify-center items-center"
      >
        <h3>
          Fill out the forms below with your remaining courses if the courses displayed in your
          timetable are less than your registered courses
        </h3>
        <a
          className="text-blue-600 hover:border-b w-52 hover:border-blue-600"
          target="_blank"
          href="https://forms.gle/GryEHErFw43oMkr7A"
          rel="noreferrer"
        >
          Click here to fill the form.
        </a>
      </div>
      <TimetableWrap ref={timetableRef}>
        <Title>
          {/* eslint-disable-next-line max-len */}
          {examTypeAndSemester?.examType} EXAMINATION TIMETABLE FOR SEMESTER{' '}
          {examTypeAndSemester?.semester}, {academicYear} ACADEMIC YEAR
        </Title>
        <p className="text-center">
          Please print this schedule and return it to the Examination center for each session
        </p>
        {data?.getStudentPapers?.map((student, index) => (
          <Row
            style={{
              marginBottom: index === 3 ? '24rem' : '',
              marginTop: index === 8 ? '24rem' : '',
            }}
            key={index}
          >
            <Details>
              <DetailItem>
                <DetailItemkey>Name</DetailItemkey> <span> : {student?.name}</span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Index Number</DetailItemkey> <span> : {student?.indexNumber}</span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Course</DetailItemkey>{' '}
                <span>
                  : {student?.paper?.courseCode} - {student?.paper?.courseName}
                </span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Time</DetailItemkey> <span>: {student?.paper?.time}</span>
              </DetailItem>
              <DetailItem>
                <DetailItemkey>Date</DetailItemkey> <span>: {student?.paper?.date}</span>
              </DetailItem>
              {!student?.allocatedRoom ? (
                <DetailItem>
                  <DetailItemkey>Room</DetailItemkey>
                  <span> : Computer Based</span>
                </DetailItem>
              ) : (
                <>
                  {student?.allocatedRoom?.name && (
                    <DetailItem>
                      <DetailItemkey>Room</DetailItemkey>
                      <span> : {student?.allocatedRoom?.name}</span>
                    </DetailItem>
                  )}
                  {student?.allocatedRoom?.college && (
                    <DetailItem>
                      <DetailItemkey>College</DetailItemkey>
                      <span> : {student?.allocatedRoom?.college}</span>
                    </DetailItem>
                  )}
                  {student?.allocatedRoom?.building && (
                    <DetailItem>
                      <DetailItemkey>Building</DetailItemkey>
                      <span> : {student?.allocatedRoom?.building}</span>
                    </DetailItem>
                  )}
                  {student?.allocatedRoom?.floor && (
                    <DetailItem>
                      <DetailItemkey>Floor</DetailItemkey>
                      <span> : {student?.allocatedRoom?.floor}</span>
                    </DetailItem>
                  )}
                </>
              )}
            </Details>
            {/* eslint-disable-next-line max-len*/}
            {student?.allocatedRoom?.qrCodeImageUrl && (
              <QRcode src={student.allocatedRoom.qrCodeImageUrl} />
            )}
          </Row>
        ))}
        {/* Add the keys to the floors here */}
      </TimetableWrap>
      <PrintButton onClick={handlePrint}>
        <PrimaryButton name="Print" iconClass="fas fa-print" />
      </PrintButton>
    </PageWrapper>
  );
};
